import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import api from '../../utils/api';
import { en } from '../../translations/en';
import { logger } from '../../utils/logger';
import { IncidentBanner } from '../IncidentBanner';

const t = en.admin.incidentBanner;

interface BannerConfig {
  enabled: boolean;
  severity: 'info' | 'warning' | 'error';
  message: string;
  details: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

const DEFAULT_CONFIG: BannerConfig = {
  enabled: false,
  severity: 'warning',
  message: '',
  details: '',
};

const IncidentBannerTab: React.FC = () => {
  const [config, setConfig] = useState<BannerConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/api/admin/incident-banner');
        setConfig(response.data);
      } catch (err) {
        logger.error('Failed to load incident banner config', err);
        setErrorMessage(t.saveError);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const response = await api.put('/api/admin/incident-banner', {
        enabled: config.enabled,
        severity: config.severity,
        message: config.message,
        details: config.details,
      });
      setConfig(response.data);
      setSuccessMessage(t.saveSuccess);
    } catch (err) {
      logger.error('Failed to save incident banner config', err);
      setErrorMessage(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled = saving || (config.enabled && !config.message.trim());

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.description}
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled}
              onChange={(e) =>
                setConfig(prev => ({ ...prev, enabled: e.target.checked }))
              }
            />
          }
          label={
            <Box>
              <Typography variant="body1">{t.enabled}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t.enabledDescription}
              </Typography>
            </Box>
          }
        />

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>{t.severity}</Typography>
          <ToggleButtonGroup
            value={config.severity}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setConfig(prev => ({ ...prev, severity: value }));
              }
            }}
            size="small"
          >
            <ToggleButton value="info">{t.severityInfo}</ToggleButton>
            <ToggleButton value="warning">{t.severityWarning}</ToggleButton>
            <ToggleButton value="error">{t.severityError}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          label={t.message}
          placeholder={t.messagePlaceholder}
          value={config.message}
          onChange={(e) =>
            setConfig(prev => ({ ...prev, message: e.target.value }))
          }
          fullWidth
          inputProps={{ maxLength: 500 }}
          error={config.enabled && !config.message.trim()}
          helperText={
            config.enabled && !config.message.trim()
              ? 'Message is required when banner is enabled'
              : `${config.message.length}/500`
          }
        />

        <TextField
          label={t.details}
          placeholder={t.detailsPlaceholder}
          value={config.details}
          onChange={(e) =>
            setConfig(prev => ({ ...prev, details: e.target.value }))
          }
          fullWidth
          multiline
          rows={4}
          inputProps={{ maxLength: 5000 }}
          helperText={`${config.details.length}/5000`}
        />

        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {saving ? t.saving : t.save}
          </Button>
        </Box>

        {(config.updatedAt || config.updatedBy) && (
          <Typography variant="caption" color="text.secondary">
            {config.updatedAt && `${t.lastUpdated}: ${new Date(config.updatedAt).toLocaleString()}`}
            {config.updatedAt && config.updatedBy && ' · '}
            {config.updatedBy && `${t.updatedBy}: ${config.updatedBy}`}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        {t.preview}
      </Typography>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <IncidentBanner
          visible={config.message.trim().length > 0}
          message={config.message}
          details={config.details}
          severity={config.severity}
          onDismiss={() => {}}
        />
        {!config.message.trim() && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            Enter a message above to see a preview
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default IncidentBannerTab;
